import {MidiNote} from "./note-selection";

/**
 * The audio seam for melody notes. The scheduler's events$ subscription
 * calls play() per note onset; stop() silences anything scheduled or
 * sounding, mirroring ClickPlayer.stop().
 */
interface NoteVoice {
    /** lengthSeconds is the note's sounding length in seconds (ADR 0003). */
    play(pitch: MidiNote, timestamp: number, lengthSeconds: number): void;
    /** Silence anything scheduled/sounding. */
    stop(): void;
}

const OSC_TYPE: OscillatorType = 'square';
/** Decay span: the note length, clamped. */
const RELEASE_MIN_SECONDS = 0.7;
const RELEASE_MAX_SECONDS = 2.0;
/** Fixed tail added to the decay span, so very short notes still decay. */
const RELEASE_TAIL_OFFSET_SECONDS = 0.5;
/** Oscillator stops this far past the note end, so the decay tail completes. */
const OSC_STOP_GRACE_PERIOD = 1.0;
/** Lowpass softens the oscillator's high harmonics. */
const LOWPASS_FREQ = 2500;
const LOWPASS_Q = 0.5;
/** The note voice's own level; clicks are left as they are. */
const VOICE_GAIN = 0.8;

/** MIDI note number to frequency in Hz. */
const midiToFrequency = (pitch: MidiNote): number => 440 * Math.pow(2, (pitch - 69) / 12);

/**
 * The v1 NoteVoice: one oscillator per note (not a single shared osc) with
 * a simplified hold-then-decay envelope (ADR 0003, amended twice):
 *
 *   attack  - instantaneous set to peak (the lowpass softens the edge, so
 *             no ramp is needed; targeting any value just re-stated the
 *             fresh node's default)
 *   release - exponential decay toward near-silence, spanning the note
 *             length clamped to 0.7s..2s, plus a fixed 0.5s tail offset
 *   stop    - the oscillator stops 1s past the note's end (grace period),
 *             so a note ending at a bar boundary completes its decay tail
 *             instead of being cut mid-decay
 *
 * The oscillator type and lowpass filter shape the timbre.
 *
 * Shares its AudioContext with ClickPlayer - the scheduler's clock is that
 * context's currentTime, so notes and clicks must schedule against the
 * same clock.
 */
class OscillatorNoteVoice implements NoteVoice {

    private readonly audioContext: AudioContext;
    // routed through a single gain node so stop() can silence everything,
    // mirroring ClickPlayer
    private gainNode: GainNode;

    constructor(audioContext: AudioContext) {
        this.audioContext = audioContext;
        this.gainNode = this.createGainNode();
    }

    play(pitch: MidiNote, timestamp: number, lengthSeconds: number) {
        const oscillator = this.audioContext.createOscillator();
        oscillator.type = OSC_TYPE;
        oscillator.frequency.value = midiToFrequency(pitch);

        const filter = this.audioContext.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = LOWPASS_FREQ;
        filter.Q.value = LOWPASS_Q;

        const envelope = this.audioContext.createGain();
        this.scheduleEnvelope(envelope.gain, timestamp, lengthSeconds);

        oscillator.connect(filter);
        filter.connect(envelope);
        envelope.connect(this.gainNode);
        oscillator.onended = () => {
            oscillator.disconnect();
            filter.disconnect();
            envelope.disconnect();
        };
        oscillator.start(timestamp);
        oscillator.stop(timestamp + lengthSeconds + OSC_STOP_GRACE_PERIOD);
    }

    /**
     * Holds at peak, then decays exponentially toward near-silence. An
     * exponential ramp must anchor on a non-zero value: anchoring at 0
     * poisons the interpolation (v1/v0 = Infinity, 0 * Infinity = NaN).
     */
    private scheduleEnvelope(gain: AudioParam, timestamp: number, lengthSeconds: number) {
        gain.setValueAtTime(1.0, timestamp);

        const decaySpan = Math.min(RELEASE_MAX_SECONDS, Math.max(RELEASE_MIN_SECONDS, lengthSeconds))
            + RELEASE_TAIL_OFFSET_SECONDS;

        gain.exponentialRampToValueAtTime(0.0001, timestamp + decaySpan);
    }

    stop() {
        this.gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime);
        this.gainNode.disconnect();
        this.gainNode = this.createGainNode();
    }

    private createGainNode(): GainNode {
        const gainNode = new GainNode(this.audioContext, {gain: VOICE_GAIN});
        gainNode.connect(this.audioContext.destination);
        return gainNode;
    }
}

export {NoteVoice, OscillatorNoteVoice, midiToFrequency};
